import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Bell, Clock, ShieldCheck, PartyPopper } from "lucide-react";
import { Button, Card, Badge, ScoreRing, ProgressTrack, Logo } from "../components/kit";
import { toast } from "sonner";
import api, { TIMEZONE, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Onboarding() {
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(-1); // -1 = intro, 0..n-1 = groups, n = summary
  const [answers, setAnswers] = useState({});
  const [deferred, setDeferred] = useState([]);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [returning, setReturning] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get("/onboarding/schema"),
      api.get("/onboarding/state").catch(() => ({ data: {} })),
    ])
      .then(([s, st]) => {
        setSchema(s.data);
        const ob = st.data || {};
        if (ob.answers && Object.keys(ob.answers).length > 0) {
          setAnswers(ob.answers);
          setDeferred(Array.isArray(ob.deferred) ? ob.deferred : []);
          setReturning(true);
        }
      })
      .catch(() => setError("Could not load onboarding."));
  }, []);

  const groups = schema?.groups || [];
  const total = schema?.total_questions || 25;
  const maxDeferrals = schema?.max_deferrals || 12;

  const answeredCount = useMemo(
    () => Object.keys(answers).filter((k) => {
      const v = answers[k];
      return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "" && v !== null;
    }).length,
    [answers]
  );

  if (error && !schema) {
    return <Centered><p style={{ color: "var(--status-risk)" }}>{error}</p></Centered>;
  }
  if (!schema) {
    return <Centered><p style={{ color: "var(--text-muted)" }}>Loading your baseline…</p></Centered>;
  }

  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const isAnswered = (q) => {
    const v = answers[q.id];
    if (q.id === "preferred_name") {
      const pn = typeof v === "string" ? v.trim() : v;
      if (pn) return true;
      return String(answers.first_name || "").trim().length > 0;
    }
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== "" && v !== null;
  };

  const continueFromGroup = (group) => {
    setError("");
    // required (non-deferrable) must be answered
    const missingRequired = group.questions.filter((q) => !q.defer && !isAnswered(q) && q.type !== "text_optional");
    if (missingRequired.length > 0) {
      setError("Please answer the required questions before continuing.");
      return;
    }
    // auto-defer any blank deferrable questions in this group
    const blanks = group.questions.filter((q) => q.defer && !isAnswered(q)).map((q) => q.id);
    const nextDeferred = Array.from(new Set([...deferred, ...blanks]));
    if (nextDeferred.length > maxDeferrals) {
      setError(`You can defer up to ${maxDeferrals} questions. Answer ${nextDeferred.length - maxDeferrals} more here to continue.`);
      return;
    }
    // remove any now-answered ones from deferred
    const cleaned = nextDeferred.filter((id) => {
      const q = groups.flatMap((g) => g.questions).find((x) => x.id === id);
      return q && !isAnswered(q);
    });
    setDeferred(cleaned);
    setStep((s) => s + 1);
  };

  const skipGroup = (group) => {
    setError("");
    const skippable = group.questions.filter((q) => q.defer).map((q) => q.id);
    const nextDeferred = Array.from(new Set([...deferred, ...skippable]));
    if (nextDeferred.length > maxDeferrals) {
      setError(`Skipping these would defer ${nextDeferred.length} of ${total}. The cap is ${maxDeferrals}. Answer a few instead.`);
      return;
    }
    setDeferred(nextDeferred);
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const finalAnswers = { ...answers };
      if (!String(finalAnswers.preferred_name || "").trim() && String(finalAnswers.first_name || "").trim()) {
        finalAnswers.preferred_name = String(finalAnswers.first_name).trim();
      }
      const finalDeferred = deferred.filter((id) => {
        if (id === "preferred_name" && String(finalAnswers.preferred_name || "").trim()) return false;
        const q = groups.flatMap((g) => g.questions).find((x) => x.id === id);
        return q && !isAnswered(q);
      });
      const { data } = await api.post("/onboarding/submit", { answers: finalAnswers, deferred: finalDeferred, timezone: TIMEZONE });
      setResult(data);
      await refreshUser();
      toast.success(returning ? "Profile updated" : "Baseline saved");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(detail ? formatApiErrorDetail(detail) : "Could not save your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- INTRO ----------
  if (step === -1) {
    return (
      <Shell deferred={deferred.length} maxDeferrals={maxDeferrals} answered={0} total={total} hideProgress>
        <Card padding="var(--space-8)" className="buktu-rise">
          <Badge tone="accent" dot>{returning ? "Update your profile" : `Your baseline · ${total} questions`}</Badge>
          <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", margin: "var(--space-4) 0 var(--space-3)" }}>
            {returning ? "Review and update your answers." : "Let's build your Business Health Score."}
          </h1>
          <p style={{ font: "var(--type-body)", color: "var(--text-muted)" }}>
            {total} quick questions across seven areas. Answer what you can right now — you can defer up to <strong>{maxDeferrals}</strong> and finish the rest later. Everything must be complete within <strong>{schema.completion_window_days} days</strong> of your first login, or your score is hidden until it is.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "var(--space-3)", margin: "var(--space-6) 0" }} className="buktu-intro-grid">
            {[
              { icon: <ShieldCheck size={18} />, t: `${maxDeferrals}-question defer cap`, d: "Up to half can wait" },
              { icon: <Clock size={18} />, t: `${schema.completion_window_days}-day deadline`, d: "To finish everything" },
              { icon: <Bell size={18} />, t: "3 reminders", d: "So nothing slips" },
            ].map((x) => (
              <div key={x.t} style={{ padding: "var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)" }}>
                <span style={{ color: "var(--buktu-green)" }}>{x.icon}</span>
                <div style={{ font: "var(--type-label)", color: "var(--buktu-green-deep)", marginTop: 8 }}>{x.t}</div>
                <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>{x.d}</div>
              </div>
            ))}
          </div>

          <div style={{ borderTop: "1px solid var(--border-hairline)", paddingTop: "var(--space-5)" }}>
            <span className="buktu-eyebrow">The reminder schedule</span>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
              {schema.notifications.map((n, i) => (
                <div key={n.name} style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start" }}>
                  <span style={{ font: "var(--type-data)", fontSize: 12, color: "var(--buktu-green)", marginTop: 2 }}>0{i + 1}</span>
                  <div>
                    <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{n.name}</span>
                    <span style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)" }}> · {n.timing}</span>
                    <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-faint)" }}>{n.purpose}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {returning && deferred.length > 0 && (
            <div data-testid="edit-deferred-nudge" style={{ marginTop: "var(--space-6)", padding: "var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-watch-soft)" }}>
              <span className="buktu-eyebrow" style={{ color: "#7a5a00" }}>Still to answer — {deferred.length}</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {deferred.map((id) => {
                  const q = groups.flatMap((g) => g.questions).find((x) => x.id === id);
                  return <Badge key={id} tone="watch">{q ? q.label.replace(/\?$/, "") : id}</Badge>;
                })}
              </div>
              <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "#7a5a00", marginTop: 8 }}>Finish these to keep your score visible and unlock daily check-ins.</div>
            </div>
          )}

          <div style={{ marginTop: "var(--space-6)", display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <Button size="lg" data-testid="onboarding-start-btn" onClick={() => setStep(0)} iconRight={<ArrowRight size={18} />}>
              {returning ? "Review & update my answers" : "Start the baseline"}
            </Button>
            {returning && (
              <Button variant="secondary" size="lg" data-testid="onboarding-dashboard-btn" onClick={() => navigate("/dashboard")}>Go to my dashboard</Button>
            )}
          </div>
        </Card>
        <style>{`@media (max-width:640px){.buktu-intro-grid{grid-template-columns:1fr !important;}}`}</style>
      </Shell>
    );
  }

  // ---------- SUMMARY ----------
  if (step >= groups.length) {
    if (!result) {
      return (
        <Shell deferred={deferred.length} maxDeferrals={maxDeferrals} answered={answeredCount} total={total}>
          <Card padding="var(--space-8)" className="buktu-rise">
            <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)" }}>Review & finish</h1>
            <p style={{ font: "var(--type-body)", color: "var(--text-muted)", marginTop: "var(--space-2)" }}>
              You answered <strong>{answeredCount}</strong> of {total}. {deferred.length > 0 ? <>You've deferred <strong>{deferred.length}</strong> — finish them within {schema.completion_window_days} days to keep your score visible.</> : "Nothing deferred — nice work."}
            </p>

            <div style={{ margin: "var(--space-6) 0" }}>
              <ProgressTrack value={answeredCount} max={total} tone="brand" height={10} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>
                <span>{answeredCount} answered</span>
                <span>{deferred.length} deferred · {maxDeferrals} max</span>
              </div>
            </div>

            {deferred.length > 0 && (
              <div style={{ padding: "var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-watch-soft)", marginBottom: "var(--space-5)" }}>
                <span className="buktu-eyebrow" style={{ color: "#7a5a00" }}>Deferred questions</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {deferred.map((id) => {
                    const q = groups.flatMap((g) => g.questions).find((x) => x.id === id);
                    return <Badge key={id} tone="watch">{q ? q.label.replace(/\?$/, "") : id}</Badge>;
                  })}
                </div>
              </div>
            )}

            {error && <div style={{ color: "var(--status-risk)", font: "var(--type-body-sm)", marginBottom: "var(--space-3)" }}>{error}</div>}
            <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
              <Button variant="secondary" data-testid="summary-back-btn" onClick={() => setStep(groups.length - 1)} iconLeft={<ArrowLeft size={16} />}>Back</Button>
              <Button size="lg" data-testid="summary-submit-btn" onClick={submit} disabled={submitting} iconRight={<ArrowRight size={18} />}>
                {submitting ? "Scoring…" : returning ? "Save my answers" : "See my score"}
              </Button>
            </div>
          </Card>
        </Shell>
      );
    }

    // scored result
    return (
      <Shell deferred={result.deferred_count} maxDeferrals={maxDeferrals} answered={answeredCount} total={total} hideProgress>
        <Card padding="var(--space-8)" className="buktu-rise" data-brand="vitals" data-testid="onboarding-result">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "var(--space-4)" }}>
            <span style={{ color: "var(--buktu-green)" }}><PartyPopper size={28} /></span>
            <ScoreRing value={result.score.overall} size={148} subLabel="of 100" />
            <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)" }}>Your Business Health Score</h1>
            <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", maxWidth: 340 }}>
              Built from your self-reported baseline. It'll sharpen as you check in daily — fifteen seconds at a time.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", marginTop: "var(--space-6)" }}>
            {Object.entries(result.score.dimensions).map(([label, v]) => (
              <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", font: "var(--type-label)", color: "var(--text-body)" }}>
                  <span>{label}</span>
                  <span style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-muted)" }}>{v}</span>
                </div>
                <ProgressTrack value={v} tone={v >= 70 ? "good" : v >= 40 ? "watch" : "risk"} height={7} />
              </div>
            ))}
          </div>

          {result.deferred_count > 0 && (
            <div style={{ padding: "var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-watch-soft)", marginTop: "var(--space-6)", font: "var(--type-body-sm)", color: "#7a5a00" }}>
              You still have <strong>{result.deferred_count}</strong> deferred question{result.deferred_count > 1 ? "s" : ""}. Finish within {schema.completion_window_days} days to keep your score visible.
            </div>
          )}

          <div style={{ marginTop: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-3)", alignItems: "center" }}>
            <Button size="lg" data-testid="result-dashboard-btn" onClick={() => navigate("/dashboard")} iconRight={<ArrowRight size={18} />}>Go to my dashboard</Button>
            <span style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)" }}>Vitals Core is free — your daily check-ins start today.</span>
          </div>
        </Card>
      </Shell>
    );
  }

  // ---------- GROUP SCREEN ----------
  const group = groups[step];
  return (
    <Shell deferred={deferred.length} maxDeferrals={maxDeferrals} answered={answeredCount} total={total} groupIndex={step} groupCount={groups.length}>
      <Card padding="var(--space-8)" className="buktu-rise" key={group.id} data-testid={`onboarding-group-${group.id}`}>
        <span className="buktu-eyebrow">{group.eyebrow}</span>
        <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", margin: "var(--space-3) 0 var(--space-2)" }}>{group.title}</h1>
        <p style={{ font: "var(--type-body)", color: "var(--text-muted)", marginBottom: "var(--space-6)" }}>{group.blurb}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
          {group.questions.map((q) => (
            <QuestionInput key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
          ))}
        </div>

        {error && <div style={{ color: "var(--status-risk)", font: "var(--type-body-sm)", marginTop: "var(--space-4)" }}>{error}</div>}

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginTop: "var(--space-8)", flexWrap: "wrap" }}>
          {step > 0 && (
            <Button variant="ghost" data-testid="group-back-btn" onClick={() => { setError(""); setStep((s) => s - 1); }} iconLeft={<ArrowLeft size={16} />}>Back</Button>
          )}
          {group.questions.some((q) => q.defer) && (
            <Button variant="secondary" data-testid="group-skip-btn" onClick={() => skipGroup(group)}>Skip for now</Button>
          )}
          <Button style={{ marginLeft: "auto" }} data-testid="group-continue-btn" onClick={() => continueFromGroup(group)} iconRight={<ArrowRight size={16} />}>
            {step === groups.length - 1 ? "Review" : "Continue"}
          </Button>
        </div>
      </Card>
    </Shell>
  );
}

/* ---------------- Question renderer ---------------- */
function QuestionInput({ q, value, onChange }) {
  const label = (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
      <span style={{ font: "var(--type-heading)", fontSize: 17, color: "var(--text-strong)" }}>{q.label}</span>
      {q.defer ? (
        <Badge tone="neutral">Optional now</Badge>
      ) : q.type !== "text_optional" ? (
        <Badge tone="accent">Required</Badge>
      ) : null}
    </div>
  );

  const hint = q.hint && <div style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-faint)", marginBottom: "var(--space-3)" }}>{q.hint}</div>;

  if (q.type === "single") {
    return (
      <div data-testid={`q-${q.id}`}>
        {label}{hint}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {q.options.map((opt) => (
            <button key={opt} type="button" className="buktu-option" data-selected={value === opt} data-testid={`q-${q.id}-opt-${opt}`} onClick={() => onChange(opt)}>
              <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, border: "2px solid " + (value === opt ? "var(--buktu-green)" : "var(--border-strong)"), background: value === opt ? "var(--buktu-green)" : "transparent", display: "grid", placeItems: "center", color: "#fff" }}>{value === opt && <Check size={11} />}</span>
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div data-testid={`q-${q.id}`}>
        {label}{hint}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
          {q.options.map((opt) => {
            const on = arr.includes(opt);
            return (
              <button key={opt} type="button" className="buktu-option" data-selected={on} data-testid={`q-${q.id}-opt-${opt}`} onClick={() => toggle(opt)} style={{ width: "auto" }}>
                <span style={{ width: 18, height: 18, borderRadius: 6, flexShrink: 0, border: "2px solid " + (on ? "var(--buktu-green)" : "var(--border-strong)"), background: on ? "var(--buktu-green)" : "transparent", display: "grid", placeItems: "center", color: "#fff" }}>{on && <Check size={11} />}</span>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const inputType = q.type === "number" || q.type === "percent" ? "number" : q.type === "date" ? "date" : "text";
  return (
    <div data-testid={`q-${q.id}`}>
      {label}{hint}
      <input
        className="buktu-input"
        type={inputType}
        value={value ?? ""}
        min={q.min}
        max={q.max}
        placeholder={q.placeholder || (q.type === "percent" ? "%" : "")}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`q-${q.id}-input`}
      />
    </div>
  );
}

/* ---------------- Shell (progress header) ---------------- */
function Shell({ children, deferred, maxDeferrals, answered, total, groupIndex, groupCount, hideProgress }) {
  return (
    <div className="buktu-root" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <div className="buktu-wrap" style={{ maxWidth: 720, paddingTop: "var(--space-8)", paddingBottom: "var(--space-16)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={26} />
          <div style={{ marginLeft: "auto" }}>
            <Badge tone={deferred >= maxDeferrals ? "risk" : "accent"} dot data-testid="deferral-counter">Deferred {deferred} / {maxDeferrals}</Badge>
          </div>
        </div>

        {!hideProgress && (
          <div style={{ marginBottom: "var(--space-6)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>
              <span>{groupCount ? `Group ${groupIndex + 1} of ${groupCount}` : "Almost there"}</span>
              <span>{answered} / {total} answered</span>
            </div>
            <ProgressTrack value={groupCount ? groupIndex + 1 : total} max={groupCount || total} tone="brand" height={8} />
          </div>
        )}

        {children}
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="buktu-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>{children}</div>
  );
}
